import Page from "components/Page";
import React, { useCallback, useEffect, useState } from "react";
import styles from "./../aisettings.module.scss";
import { withTranslation } from "react-i18next";
import constants from "./../../../../constants";
import Text from "components/Text";
import Table from "components/table";
import Empty from "components/Empty";
import moment from "moment";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import UploadModal from "components/CustomModal/UploadModal";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import {
  deleteDocument,
  getCategoryList,
  uploadDocuments,
  useGetDocumentsList,
} from "repositories/ai-assistant-repository";
import useUploadService from "hooks/useUploadService";
import Loader from "components/Loader";
import useHandleApiError from "hooks/useHandleApiError";
import { formatLocalDateTime, handleError } from "utils";
import { Link } from "react-router-dom";

const PAGE_SIZE = constants.pageSize;
const containerName = "ai-assistant";

function ManageDocuments({ history, t }) {
  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [confirmDelete, setConfirmDeleteModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [categoryList, setCategoryList] = useState([]);

  const { upload: uploadImage } = useUploadService();

  const {
    isLoading,
    data,
    refetch,
    isFetching,
    error: isError,
  } = useGetDocumentsList(pageNumber, PAGE_SIZE, searchTerm);

  useHandleApiError(isLoading, isFetching, isError);
  const goBack = () =>
    history.push({ pathname: constants.routes.aiAssistant.aiSetting });

  const fetchCategoryList = async () => {
    try {
      const response = await getCategoryList();
      setCategoryList(response);
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    fetchCategoryList();
  }, []);

  const columns = [
    {
      attrs: { datatitle: t("aiAssistant.documentName") },
      dataField: "documentName",
      text: t("aiAssistant.documentName"),
      formatter: (cellContent, row) => {
        if (!isLoading)
          return (
            <Link
              to={{
                pathname: constants.routes.aiAssistant.viewManageAiDocuments,
                state: {
                  documentId: row?.id,
                  isFromSuperAdmin: true,
                  categoryList,
                  documentList: data?.items,
                },
              }}
            >
              {cellContent}
            </Link>
          );
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.description") },
      dataField: "description",
      text: t("aiAssistant.description"),
      formatter: (cellContent) => {
        if (!isLoading)
          return cellContent ? (
            <span className="pms_table_data truncate_text_line_clam_3">
              {cellContent}
            </span>
          ) : (
            "--"
          );
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.version") },
      dataField: "version",
      text: t("aiAssistant.version"),
      formatter: (cellContent) => {
        if (!isLoading) return <span>{cellContent ? cellContent : "--"}</span>;
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("superAdminCategories.categoryName") },
      dataField: "categoryName",
      text: t("superAdminCategories.categoryName"),
      formatter: (cellContent) => {
        if (!isLoading) return <span>{cellContent ? cellContent : "--"}</span>;
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.fileType") },
      dataField: "documentType",
      text: t("aiAssistant.fileType"),
      formatter: (cellContent) => {
        if (!isLoading) return cellContent ? cellContent : "--";
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.uploadedOn") },
      dataField: "createdAt",
      text: t("aiAssistant.uploadedOn"),
      formatter: (cellContent) => {
        if (!isLoading)
          return cellContent
            ? formatLocalDateTime(cellContent, "MMM DD, YYYY")
            : "--";
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.expirationDate") },
      dataField: "expirationDate",
      text: t("aiAssistant.expirationDate"),
      formatter: (cellContent) => {
        if (!isLoading)
          return (
            <span>
              {cellContent ? moment(cellContent).format("MMM Do, YYYY") : "--"}
            </span>
          );
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      formatter: (cellContent, row) => {
        if (isLoading)
          return <div className="text-placeholder-100 shimmer-animation"></div>;
        return (
          <span
            className="pointer"
            onClick={() => deleteAIDocument(row?.id)}
            style={{
              fontSize: "12px",
              fontWeight: "500",
              color: "#e76f2a",
            }}
          >
            {" "}
            <u>{t("Delete")}</u>
          </span>
        );
      },
    },
  ];

  const handleSearchFilter = (event) => {
    if (event.target.value?.trim()) {
      setSearchTerm(event.target.value);
    } else {
      setSearchTerm("");
    }
  };

  const uploadFilesToAPI = async (fileObjects) => {
    let aiAssistantProjectDocuments = [];

    for (let i = 0; i < fileObjects.length; i++) {
      let fileObj = fileObjects[i];

      if (fileObj.file && !fileObj.isUploaded) {
        try {
          const [err, blobData] = await uploadImage(
            fileObj.file,
            containerName,
          );

          if (err) {
            console.error("Upload error for file:", fileObj.documentName, err);
            throw new Error(err);
          }

          aiAssistantProjectDocuments.push({
            documentPath: `${containerName}/${blobData.blobName}`,
            documentName: fileObj.documentName,
            description: fileObj.description || "",
          });

          fileObj.documentPath = blobData.blobUrl;
          fileObj.isUploaded = true;
          fileObj.file = null;
        } catch (error) {
          console.error("Error uploading file:", fileObj.documentName, error);
          fileObj.errors.push(`Upload failed: ${error.message}`);
        }
      } else if (fileObj.documentPath && fileObj.isUploaded) {
        aiAssistantProjectDocuments.push({
          documentPath: fileObj.documentPath,
          documentName: fileObj.documentName,
          description: fileObj.description || "",
        });
      }
    }

    return aiAssistantProjectDocuments;
  };

  const handleSave = async (uploadedFiles) => {
    try {
      setShowLoader(true);

      const uploadedFilesWithPaths = await uploadFilesToAPI(uploadedFiles);
      const aiAssistantProjectDocuments = uploadedFiles?.map(
        (fileData, index) => ({
          documentPath:
            uploadedFilesWithPaths[index]?.documentPath ||
            fileData.documentPath,
          DocumentName: fileData.documentName,
          Description: fileData.Description,
          Version: fileData.Version,
          Category: fileData.Category,
          ExpirationDate: fileData.ExpirationDate,
          IsForAllOffice: true,
          IsForAllDesignation: true,
        }),
      );
      const payload = {
        AiAssistantProjectDocuments: aiAssistantProjectDocuments,
      };

      await uploadDocuments(payload);

      refetch();
      setShowLoader(false);
      setIsUploadModalOpen(false);
    } catch (error) {
      setShowLoader(false);
    }
  };

  const deleteAIDocument = (documentId) => {
    setSelectedId(documentId);
    setConfirmDeleteModal(true);
  };

  const confirmDeleteMessage = async () => {
    setShowLoader(true);
    try {
      const resp = await deleteDocument(selectedId);
      setConfirmDeleteModal(false);
      setShowLoader(false);
      toast.success(resp?.message);
      refetch();
    } catch (e) {
      setConfirmDeleteModal(false);
      setShowLoader(false);
      toast.error(e.message);
    }
  };

  const handleClose = () => {
    setIsUploadModalOpen(false);
  };

  const closeDeleteModal = () => {
    setConfirmDeleteModal(false);
  };

  const optimisedSearch = useCallback(debounce(handleSearchFilter, 1000));

  let rows = [];
  let totalItems = 0;
  if (!isLoading && data && data?.items) {
    rows = data.items;
    totalItems = data?.totalCount;
  } else {
    rows = new Array(PAGE_SIZE).fill({});
    totalItems = PAGE_SIZE;
  }

  return (
    <>
      {(showLoader || isFetching) && <Loader />}
      <Page
        onBack={() => goBack()}
        className={styles["upload-document"]}
        title={t("aiAssistant.manageDocument")}
      >
        <Text size="14px" weight="300" color="#000000">
          {t("aiAssistant.aiAssistSubheading")}
        </Text>
        <div className={styles["upload-document-btn"]}>
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="button  button-round button-shadow"
          >
            {t("aiAssistant.uploadDocument")}
          </button>
        </div>
        <div className="search-box mb-4">
          <input
            type="text"
            placeholder={t("aiAssistant.search")}
            onInput={optimisedSearch}
          />
          <span className="ico">
            <img
              src={require("assets/images/search-icon.svg").default}
              alt="icon"
            />
          </span>
        </div>
        <div className="data-list">
          <Table
            columns={columns}
            data={rows}
            keyField="id"
            handlePagination={setPageNumber}
            pageNumber={pageNumber}
            totalItems={totalItems}
            pageSize={PAGE_SIZE}
          />
          {!isLoading && totalItems === 0 && (
            <Empty
              Message={t("aiAssistant.noRecord", {
                item: t("aiAssistant.document"),
              })}
            />
          )}
        </div>
        {isUploadModalOpen && (
          <UploadModal
            isOpen={isUploadModalOpen}
            setIsOpen={() => setIsUploadModalOpen(!isUploadModalOpen)}
            onClose={handleClose}
            onSave={handleSave}
            existingDocuments={data?.items || []}
            categoryList={categoryList}
            fromSuperAdmin={true}
          />
        )}
        {confirmDelete && (
          <DeleteConfirmationModal
            closeDeleteModal={closeDeleteModal}
            confirmDelete={confirmDelete}
            confirmDeleteMessage={confirmDeleteMessage}
            deleteMessage={t("aiAssistant.deleteMessage")}
            title={t("aiAssistant.deleteModalTitle")}
            btnText={t("delete")}
          />
        )}
      </Page>
    </>
  );
}

export default withTranslation()(ManageDocuments);
